import React, { forwardRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { TransitionProps } from '@mui/material/transitions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { mediaQueries } from '../../../../theme';
import {
  EMAIL_VERIFICATION_COLOR as COLOR,
  EMAIL_VERIFICATION_COPY as COPY,
  EMAIL_VERIFICATION_METRICS as METRICS,
} from '../EmailVerification/emailVerification.constants';

/** Open: scale up from slightly below centre. Close: quicker, no overshoot. */
const PopTransition = forwardRef(function PopTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return (
    <Grow
      ref={ref}
      {...props}
      style={{ transformOrigin: 'center 60%', ...props.style }}
      easing={{ enter: 'cubic-bezier(0.22, 1, 0.36, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
    />
  );
});

/** Phone: a bottom sheet that slides up, and back down on close. */
const SheetTransition = forwardRef(function SheetTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide
      ref={ref}
      direction="up"
      {...props}
      easing={{ enter: 'cubic-bezier(0.22, 1, 0.36, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
    />
  );
});

export const primaryButtonSx = {
  height: METRICS.buttonHeight,
  borderRadius: `${METRICS.buttonRadius}px`,
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 600,
  boxShadow: 'none',
  backgroundColor: COLOR.brand,
  color: COLOR.onBrand,
  '&:hover': { backgroundColor: COLOR.brandHover, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: COLOR.disabled, color: COLOR.onBrand },
} as const;

export const textLinkSx = {
  color: COLOR.brand,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
} as const;

/** Heading and one line under it, at the top of each step. */
export function VerificationTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <>
      <Box component="h2" sx={{ m: 0, mb: 0.5, pr: 5, fontSize: { xs: 24, sm: 26 }, lineHeight: 1.25, fontWeight: 700, color: COLOR.brand }}>
        {title}
      </Box>
      <Box component="p" sx={{ m: 0, mb: 2.5, fontSize: 14, color: COLOR.muted }}>
        {subtitle}
      </Box>
    </>
  );
}

type VerificationDialogProps = {
  open: boolean;
  /** Blocked while a request is in flight. */
  busy: boolean;
  onClose: () => void;
  /** After the close animation: reset the steps here so the dialog never flashes back. */
  onExited: () => void;
  /** Changing it fades the content in, e.g. the current step. */
  stepKey: string;
  children: React.ReactNode;
};

/**
 * Shell shared by the email and mobile verification dialogs: close button and fade between
 * steps. A centred pop-up on tablet and desktop; on a phone, a bottom
 * sheet that slides up from the bottom edge.
 */
function VerificationDialog({ open, busy, onClose, onExited, stepKey, children }: VerificationDialogProps) {
  const sheet = useMediaQuery(mediaQueries.mobile, { noSsr: true });
  const close = () => {
    if (!busy) onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      slots={{ transition: sheet ? SheetTransition : PopTransition }}
      transitionDuration={{ enter: METRICS.enterMs, exit: METRICS.exitMs }}
      fullWidth
      maxWidth="xs"
      data-layout={sheet ? 'sheet' : 'dialog'}
      sx={sheet ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      slotProps={{
        transition: { onExited },
        backdrop: { sx: { backgroundColor: COLOR.scrim, backdropFilter: 'blur(3px)' } },
        paper: {
          sx: sheet
            ? {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '92dvh',
                borderRadius: `${METRICS.radius}px ${METRICS.radius}px 0 0`,
                px: 3,
                pt: 3.5,
                // Clear of the iPhone home bar.
                pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                position: 'relative',
                boxShadow: `0 -12px 40px ${COLOR.shadow}`,
                overflowX: 'hidden',
                overflowY: 'auto',
              }
            : {
                borderRadius: `${METRICS.radius}px`,
                p: { xs: 3, sm: 3.5 },
                position: 'relative',
                boxShadow: `0 24px 60px ${COLOR.shadow}`,
                overflow: 'hidden',
              },
        },
      }}
    >
      {sheet ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: COLOR.fieldHover,
          }}
        />
      ) : null}
      <IconButton
        onClick={close}
        disabled={busy}
        size="small"
        aria-label={COPY.close}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          backgroundColor: COLOR.field,
          transition: 'transform 200ms ease, background-color 200ms',
          '&:hover': { backgroundColor: COLOR.fieldHover, transform: 'rotate(90deg)' },
          '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>

      <Fade in key={stepKey} timeout={METRICS.stepFadeMs}>
        <Box>{children}</Box>
      </Fade>
    </Dialog>
  );
}

export default VerificationDialog;
